import { useEffect, useRef, useState } from "react";
import { Mic, MicOff, Square, Trash2, Send, Pause, Play } from "lucide-react";

/**
 * In-browser voice recorder.
 *
 * Hands the parent a Blob (audio/webm or audio/mp4 depending on browser)
 * via `onRecorded(blob, durationSec)`. Includes preview-before-save so a
 * user can listen back and re-record before committing.
 *
 * Works on Android Chrome + iOS Safari (they speak different mime types,
 * so we pick whichever the browser will record).
 */
function pickMimeType() {
  if (typeof MediaRecorder === "undefined") return null;
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4;codecs=mp4a.40.2",
    "audio/mp4",
    "audio/ogg;codecs=opus",
  ];
  for (const t of candidates) {
    if (MediaRecorder.isTypeSupported(t)) return t;
  }
  return "";
}

export default function VoiceRecorder({ onRecorded, onCancel, autoStart = true, label = "Voice note" }) {
  const [state, setState] = useState("idle"); // idle | recording | preview | error
  const [duration, setDuration] = useState(0);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const tickRef = useRef(null);
  const blobRef = useRef(null);

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const startRecording = async () => {
    setErrorMsg("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = pickMimeType();
      const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      mr.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, {
          type: mime || "audio/webm",
        });
        blobRef.current = blob;
        setPreviewUrl(URL.createObjectURL(blob));
        setState("preview");
        stopStream();
      };
      mr.start();
      recorderRef.current = mr;
      setDuration(0);
      setState("recording");
      tickRef.current = setInterval(() => setDuration((d) => d + 1), 1000);
    } catch (e) {
      setErrorMsg(
        e?.name === "NotAllowedError"
          ? "Microphone permission was denied. Allow it in the browser settings to record."
          : "Couldn't access the microphone.",
      );
      setState("error");
    }
  };

  const stopRecording = () => {
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
    }
  };

  // Auto-start on mount when requested.
  useEffect(() => {
    if (autoStart) startRecording();
    return () => {
      if (tickRef.current) clearInterval(tickRef.current);
      stopStream();
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reset = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    blobRef.current = null;
    setPreviewUrl(null);
    setDuration(0);
    setState("idle");
  };

  const accept = () => {
    if (!blobRef.current) return;
    onRecorded?.(blobRef.current, duration);
  };

  return (
    <div
      className="border border-white/10 rounded-md p-4 bg-white/[0.02]"
      data-testid="voice-recorder"
    >
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="text-xs uppercase tracking-[0.18em] text-[#a0aabf] font-bold flex items-center gap-1.5">
          <Mic className="w-3.5 h-3.5 text-[#1fb6c1]" /> {label}
        </div>
        <div className="text-xs font-mono text-[#1fb6c1]" data-testid="voice-recorder-duration">
          {Math.floor(duration / 60)
            .toString()
            .padStart(2, "0")}
          :{(duration % 60).toString().padStart(2, "0")}
        </div>
      </div>

      {state === "error" && (
        <div className="text-xs text-rose-300 mb-3">{errorMsg}</div>
      )}

      {state === "idle" && (
        <button
          type="button"
          onClick={startRecording}
          className="hv-btn hv-btn-primary w-full py-3"
          data-testid="voice-recorder-start"
        >
          <Mic className="w-4 h-4" /> Start recording
        </button>
      )}

      {state === "recording" && (
        <div className="flex flex-col items-center gap-3 py-2">
          <div className="relative">
            <div className="absolute inset-0 rounded-full bg-rose-500/30 animate-ping" />
            <div className="relative w-14 h-14 rounded-full bg-rose-500 flex items-center justify-center">
              <MicOff className="w-6 h-6 text-white" />
            </div>
          </div>
          <button
            type="button"
            onClick={stopRecording}
            className="hv-btn hv-btn-ghost text-xs"
            data-testid="voice-recorder-stop"
          >
            <Square className="w-3.5 h-3.5" /> Stop
          </button>
        </div>
      )}

      {state === "preview" && previewUrl && (
        <div className="space-y-3">
          <audio
            src={previewUrl}
            controls
            className="w-full"
            data-testid="voice-recorder-preview-audio"
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={reset}
              className="hv-btn hv-btn-ghost text-xs flex-1"
              data-testid="voice-recorder-redo"
            >
              <Trash2 className="w-3.5 h-3.5" /> Re-record
            </button>
            <button
              type="button"
              onClick={accept}
              className="hv-btn hv-btn-primary text-xs flex-1"
              data-testid="voice-recorder-accept"
            >
              <Send className="w-3.5 h-3.5" /> Use this
            </button>
          </div>
        </div>
      )}

      {onCancel && (
        <div className="mt-3 text-center">
          <button
            type="button"
            onClick={() => {
              stopRecording();
              onCancel?.();
            }}
            className="text-[11px] text-[#647087] hover:text-white"
            data-testid="voice-recorder-cancel"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}

// Re-export for convenience.
export { Mic, Pause, Play };
